import React, { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CalendarDays,
  Search,
  X,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import UserTable from "./UserTable";
import CompanyTable from "./CompanyTable";
import DepartmentTable from "./DepartmentTable";
import AddUserModal from "../Modals/AddUserModal";
import ExportHistoryTable from "./ExportHistoryTable";
import {
  getCompaniesWiseDetails,
  getDepartmentWiseDetails,
  getUserDetailsData,
  getCompaniesForDataAccess,
  getDepartmentsForDataAccess,
  exportUsersExcelReport,
  exportUsersExcelReportHistory,
  getCompanies,
  exportCompaniesExcelReport,
} from "../../requests";
import styles from "../../_css/DataAccessAndReport.module.css";
import SearchDropdown from "../common/SearchDropdown";
import rpStyles from "../../_css/RoleplayManagement.module.css";
import PaginationUI from "../UI/PaginationUI";
import { deleteAuthToken, notifyError, notifyInfo } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch } from "react-redux";
import DataLoader from "../common/DataLoader";

const Index = () => {
  const [page, setPage] = useState(1);
  const [showDropdown, setDropdown] = useState(false);
  const [showFilterDropdown, setFilterDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("All Companies");
  const [selectedDepartment, setSelectedDepartment] =
    useState("All Departments");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [exportHistoryData, setExportHistoryData] = useState([]);
  const [exportHistoryTotalPages, setExportHistoryTotalPages] = useState(0);
  const [exportHistoryPage, setExportHistoryPage] = useState(1);
  const [viewType, setViewType] = useState("Users");
  const [showViewTypeDropdown, setShowViewTypeDropdown] = useState(false);
  const [tableDataUsers, setTableDataUsers] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const dropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const viewTypeDropdownRef = useRef(null);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchExportHistory();
  }, [exportHistoryPage]);

  // Fetch data when filters change
  useEffect(() => {
    fetchData();
  }, [viewType, selectedCompany, selectedDepartment, fromDate, toDate, page]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setFilterDropdown(false);
      }
      if (
        viewTypeDropdownRef.current &&
        !viewTypeDropdownRef.current.contains(event.target)
      ) {
        setShowViewTypeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch data based on filters
  const fetchData = async () => {
    setLoading(true);
    try {
      const filters = {
        company_id: selectedCompanyId !== null ? selectedCompanyId : "",
        department_id:
          selectedDepartmentId !== null ? selectedDepartmentId : "",
        search: getSearchTerm(),
        page: page,
        limit: rowsPerPage,
      };

      if (viewType === "Users") {
        const response = await getUserDetailsData(filters);
        setTableDataUsers(response.data || []);
        setTotalPages(response.totalPages || 0);
      } else {
        // Fetch companies data with search
        const response = await getCompanies({
          page,
          rowsPerPage,
        });
        if (response?.data?.success) {
          setTableData(response.data.companies);
          setTotalPages(response.data.totalPages);
        }
      }
      // reset selections when page data changes
      setSelectedUserIds([]);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response.status === 401) {
        deleteAuthToken();
        navigate("/login");
        notifyError("Session expired, please login again.");
      }
      setTableData([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchExportHistory = async () => {
    setLoadingHistory(true);

    try {
      const response = await exportUsersExcelReportHistory({
        page: exportHistoryPage,
        limit: rowsPerPage,
      });
      setExportHistoryData(response.data || []);
      setExportHistoryTotalPages(response.pagination.totalPages || 0);
    } catch (error) {
      if (error.response.status === 401) {
        deleteAuthToken();
        navigate("/login");
        notifyError("Session expired, please login again.");
      }
      console.error("Error fetching export history:", error);
    }
    setLoadingHistory(false);
  };

  // Handle view type change
  const handleViewTypeChange = (type) => {
    setViewType(type);
    setShowViewTypeDropdown(false);
    // Clear filters when switching to Companies
    clearAllFilters();
  };

  const handleExportClick = async () => {
    if (selectedUserIds.length === 0) {
      notifyInfo(
        `Please select at least one ${viewType === "Users" ? "user" : "company"
        } to export.`
      );
      return;
    }
    dispatch(startLoader());
    try {
      if (isUserView) {
        await exportUsersExcelReport({
          userIds: selectedUserIds,
        });
      } else {
        await exportCompaniesExcelReport({
          companyIds: selectedUserIds,
        });
      }
      fetchExportHistory();
    } catch (error) {
      if (error.response.status === 401) {
        deleteAuthToken();
        navigate("/login");
        notifyError("Session expired, please login again.");
      }
      console.error("Error exporting users excel:", error);
    }
    dispatch(stopLoader());
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCompany("All Companies");
    setSelectedDepartment("All Departments");
    setSelectedCompanyId(null);
    setSelectedDepartmentId(null);
    setFromDate(null);
    setToDate(null);
    setCompanySearch("");
    setDepartmentSearch("");
    setExportHistoryPage(1);
    setPage(1);
  };

  // Check if any filter is active
  const isFilterActive = () => {
    return (
      selectedCompany !== "All Companies" ||
      selectedDepartment !== "All Departments" ||
      fromDate !== null ||
      toDate !== null
    );
  };

  // Handle company selection from SearchDropdown
  const handleCompanySelect = (option) => {
    if (!option) return;
    setSelectedCompany(option.label);
    setSelectedCompanyId(option.value);
    // Reset department when company changes
    setSelectedDepartment("All Departments");
    setSelectedDepartmentId(null);
    setPage(1);
  };

  // Handle department selection from SearchDropdown
  const handleDepartmentSelect = (option) => {
    if (!option) return;
    setSelectedDepartment(option.label);
    setSelectedDepartmentId(option.value);
    setPage(1);
  };
  // Get search term based on selected table type
  const getSearchTerm = () => {
    if (viewType === "Users") {
      return companySearch;
    } else if (viewType === "Department") {
      return departmentSearch;
    } else {
      return companySearch;
    }
  };

  const isUserView = viewType === "Users";
  const hasTableData = isUserView
    ? tableDataUsers.length > 0
    : tableData.length > 0;

  // Pagination logic for users/companies table
  const startIndex = (page - 1) * rowsPerPage;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleRowSelection = (newIds) => {
    setSelectedUserIds(newIds);
  };

  const handleExportHistoryPageChange = (newPage) => {
    console.log("newPage: ", newPage);
    if (newPage >= 1 && newPage <= exportHistoryTotalPages)
      setExportHistoryPage(newPage);
  };

  return (
    <div className={styles.dataAccessRoot}>
      <div className={styles.dataAccessSection}>
        <div className={styles.dataAccessCard}>
          <div className={styles.dataAccessCardHeader}>
            <div className={styles.dataAccessHeaderLeft}>
              {/* View Type Dropdown (Users/Companies) */}
              <div
                ref={viewTypeDropdownRef}
                className={styles.dataAccessTableTypeDropdown}
              >
                <button
                  type="button"
                  className={styles.dataAccessTableTypeButton}
                  onClick={() => setShowViewTypeDropdown(!showViewTypeDropdown)}
                >
                  {viewType}{" "}
                  {showViewTypeDropdown ? (
                    <ChevronUp size={16} color="#6b7280" />
                  ) : (
                    <ChevronDown size={16} color="#6b7280" />
                  )}
                </button>
                {showViewTypeDropdown && (
                  <div className={styles.dataAccessTableTypeMenu}>
                    <div
                      className={styles.dataAccessTableTypeMenuItem}
                      onClick={() => handleViewTypeChange("Users")}
                    >
                      Users
                    </div>
                    <div
                      className={styles.dataAccessTableTypeMenuItem}
                      onClick={() => handleViewTypeChange("Companies")}
                    >
                      Companies
                    </div>
                  </div>
                )}
              </div>

              {/* Company Search Dropdown - Only show when Users is selected */}
              {viewType === "Users" && (
                <SearchDropdown
                  placeholder="Select Company"
                  selectedLabel={
                    selectedCompany === "All Companies" ? "" : selectedCompany
                  }
                  onSelect={handleCompanySelect}
                  fetchOptions={async (search) => {
                    const response = await getCompaniesForDataAccess({
                      search,
                    });
                    return response.data || [];
                  }}
                  className={{
                    wrapper: styles.dataAccessTableTypeDropdown,
                    button: styles.dataAccessTableTypeButton,
                    dropdown: styles.dataAccessTableTypeMenu,
                    searchWrapper: styles.dataAccessFilterSearchWrapper,
                    searchInput: styles.dataAccessFilterSearchInput,
                    searchIcon: styles.dataAccessFilterSearchIcon,
                    options: styles.dataAccessFilterOptions,
                    option: styles.dataAccessFilterOption,
                    noResults: styles.dataAccessFilterNoResults,
                  }}
                />
              )}

              {/* Department Search Dropdown - Only show when Users is selected */}
              {viewType === "Users" && (
                <SearchDropdown
                  placeholder="Select Department"
                  selectedLabel={
                    selectedDepartment === "All Departments"
                      ? ""
                      : selectedDepartment
                  }
                  onSelect={handleDepartmentSelect}
                  fetchOptions={async (search) => {
                    if (!selectedCompanyId) return [];
                    const response = await getDepartmentsForDataAccess({
                      company_id: selectedCompanyId,
                      search,
                    });
                    return response.data || [];
                  }}
                  disabled={!selectedCompanyId}
                  className={{
                    wrapper: styles.dataAccessFilterDropdownWrapper,
                    button: styles.dataAccessFilterButton,
                    dropdown: styles.dataAccessFilterDropdown,
                    searchWrapper: styles.dataAccessFilterSearchWrapper,
                    searchInput: styles.dataAccessFilterSearchInput,
                    searchIcon: styles.dataAccessFilterSearchIcon,
                    options: styles.dataAccessFilterOptions,
                    option: styles.dataAccessFilterOption,
                    noResults: styles.dataAccessFilterNoResults,
                  }}
                />
              )}

              {/* Clear Filters Button - Only show when Users is selected */}
              {viewType === "Users" && isFilterActive() && (
                <button
                  onClick={clearAllFilters}
                  className={styles.dataAccessClearFiltersButton}
                >
                  <X size={16} />
                  Clear Filters
                </button>
              )}
            </div>

            <div>
              <button
                onClick={handleExportClick}
                className={styles.dataAccessExportButton}
              >
                Export Data
              </button>
            </div>
          </div>

          {/* Dynamic Table Rendering */}
          {!loading ? (
            <div className={styles.dataAccessTableWrapper}>
              {viewType === "Users" ? (
                <UserTable
                  filtereddata={tableDataUsers}
                  startIndex={startIndex}
                  selectedIds={selectedUserIds}
                  onSelectionChange={handleRowSelection}
                />
              ) : (
                <CompanyTable
                  filtereddata={tableData}
                  startIndex={startIndex}
                  selectedIds={selectedUserIds}
                  onSelectionChange={handleRowSelection}
                />
              )}
            </div>
          ) : (
            <div className="loading-container">
              <DataLoader />
            </div>
          )}

          {/* Pagination - same design as RoleManager */}
          <PaginationUI
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            hasData={hasTableData}
          />
        </div>
        {/* rendering modal*/}
        <AddUserModal
          show={showAddUserModal}
          onHide={() => setShowAddUserModal(false)}
        />
      </div>
      <div className={styles.dataAccessExportHistoryWrapper}>
        {!loadingHistory ? (
          <ExportHistoryTable
            isUserView={isUserView}
            exportHistoryData={exportHistoryData}
          />
        ) : (
          <div className="loading-container">
            <DataLoader />
          </div>
        )}
        <PaginationUI
          currentPage={exportHistoryPage}
          totalPages={exportHistoryTotalPages}
          onPageChange={handleExportHistoryPageChange}
          hasData={exportHistoryData.length > 0}
        />
      </div>
    </div>
  );
};

export default Index;
